import React,{useEffect,useRef,useState} from 'react';
import {View,Text,TextInput,Image,TouchableOpacity,ScrollView,KeyboardAvoidingView,Platform} from './rn';
import {AppState} from 'react-native';
import AppSheet from './AppSheet';
import MessageVoice from './MessageVoice';
import MessageVideo from './MessageVideo';
import {api,type ForwardMediaAsset,type ForwardMediaSource} from '../services/api';
import {forwardRequestId,completeForwardRequest} from '../media/forwardRequest';
import {useTheme} from '../theme/ThemeContext';
import {useTranslation} from '../i18n/I18nContext';
import {voiceNoteCost} from '../voice/policy';
import {formatMoney} from '../data/currencies';

export default function ForwardMediaComposer({token,myId,otherId,source,onClose,onSend}: {
  token:string;myId:number;otherId:number;source:ForwardMediaSource;onClose:()=>void;
  onSend:(asset:ForwardMediaAsset,caption:string,stamp:{amount:number;currency:string})=>Promise<boolean>;
}) {
  const colors=useTheme(),{t,lang}=useTranslation();
  const {sourceId,sourceRevision,kind}=source;
  const closeRef=useRef(onClose);
  useEffect(()=>{closeRef.current=onClose;},[onClose]);
  const [draft,setDraft]=useState<ForwardMediaAsset|null>(null),[caption,setCaption]=useState('');
  const [quote,setQuote]=useState<{amount:number;currency:string}|null>(null);
  const [state,setState]=useState<'loading'|'waiting'|'error'|'ready'|'sending'>('loading');
  const [attempt,setAttempt]=useState(0),[foreground,setForeground]=useState(AppState.currentState!=='background'&&AppState.currentState!=='inactive');
  const alive=useRef(true),busy=useRef(false),epoch=useRef(0);
  useEffect(()=>{alive.current=true;return()=>{alive.current=false;};},[]);
  useEffect(()=>{const sub=AppState.addEventListener('change',value=>{
    epoch.current++;setForeground(value==='active');setDraft(null);
  });return()=>sub.remove();},[]);
  useEffect(()=>{
    const generation=++epoch.current;let active=true;
    const current=()=>active&&alive.current&&epoch.current===generation;
    setDraft(null);setQuote(null);setState('loading');
    if(!foreground)return()=>{active=false;};
    const selected:ForwardMediaSource={sourceId,sourceRevision,kind};
    (async()=>{
      try {
        const requestId=await forwardRequestId(myId,otherId,selected);
        if(!current())return;
        let progress=await api.prepareMediaForward(token,{sourceId,sourceRevision,destinationId:otherId,requestId});
        if(!current())return;
        // Bounded foreground work only. Uncertain results stop for an explicit
        // retry; the server then inspects the reserved destination, never recopies.
        for(let n=0;n<4;n++) {
          progress=await api.advanceMediaForward(token,progress.operation);
          if(!current())return;
          if(progress.state==='sent'&&Number.isSafeInteger(progress.messageId)&&Number(progress.messageId)>0){
            await completeForwardRequest(myId,otherId,selected).catch(()=>{});
            if(current())closeRef.current();return;
          }
          if(progress.draft){
            if(progress.state!=='verified'||progress.draft.destinationId!==otherId||progress.draft.kind!==kind
              ||progress.draft.operationId!==progress.operation)throw Error('Invalid forward draft');
            const price=await api.getMessagePrice(token,otherId);
            if(!current())return;
            if(progress.draft.kind==='voice'&&(price.voice_per_minute==null||!price.voice_currency
              ||!Number.isSafeInteger(progress.draft.durationSeconds)||Number(progress.draft.durationSeconds)<=0))throw Error('Missing voice price or duration');
            const stamp=progress.draft.kind==='voice'
              ?{amount:voiceNoteCost(progress.draft.durationSeconds??0,Number(price.voice_per_minute)),currency:price.voice_currency!}
              :{amount:Number(price.amount),currency:price.currency};
            if(!Number.isFinite(stamp.amount)||stamp.amount<0||!stamp.currency)throw Error('Missing price');
            setQuote(stamp);setDraft(progress.draft);setState('ready');return;
          }
          if(progress.state!=='continuation'&&progress.state!=='verify')break;
        }
        setState(progress.state==='revoked'?'error':'waiting');
      }catch{if(current())setState('error');}
    })();
    return()=>{active=false;};
  },[token,myId,otherId,sourceId,sourceRevision,kind,attempt,foreground]);
  async function send(){
    if(!draft||!quote||busy.current||!foreground||state!=='ready')return;
    busy.current=true;setState('sending');const generation=epoch.current;
    try {
      if(await onSend(draft,caption.trim(),quote)){
        await completeForwardRequest(myId,otherId,source).catch(()=>{});
        if(alive.current&&generation===epoch.current)onClose();
      }else if(alive.current&&generation===epoch.current)setState('ready');
    }catch{if(alive.current&&generation===epoch.current){setDraft(null);setState('error');}}
    finally{busy.current=false;}
  }
  const label={color:colors.black,fontSize:16};
  return <AppSheet visible scrollable={false} title={t('message.forwardOwn')} testID="forward-media" onClose={()=>{if(!busy.current)onClose();}}
    done={{label:t('message.sendMessageAction'),onPress:send,disabled:state!=='ready'||!foreground,testID:'forward-media-send'}}>
    <KeyboardAvoidingView style={{flex:1,minHeight:0}} behavior={Platform.OS==='ios'?'padding':undefined}>
    <ScrollView keyboardShouldPersistTaps="handled" keyboardDismissMode="on-drag" contentContainerStyle={{paddingBottom:32}}>
    <View style={{padding:20,gap:16}}>
      <Text style={label}>{t('message.forwardMediaHint')}</Text>
      {!!quote&&<Text testID="forward-media-price" style={label}>{t('message.priceNoticeMessage',{amount:formatMoney(quote.amount,quote.currency,lang)})}</Text>}
      {!draft&&<Text testID="forward-media-state" style={label}>{t(state==='loading'?'common.loading':state==='waiting'?'message.forwardMediaWaiting':'message.forwardMediaUnavailable')}</Text>}
      {(state==='waiting'||state==='error')&&<TouchableOpacity testID="forward-media-retry" accessibilityRole="button"
        style={{minHeight:48,justifyContent:'center'}} onPress={()=>setAttempt(n=>n+1)}><Text style={label}>{t('common.retry')}</Text></TouchableOpacity>}
      {source.kind==='image'&&<TextInput testID="forward-media-caption" value={caption} onChangeText={setCaption}
        multiline maxLength={4000} editable={state!=='sending'} accessibilityLabel={t('message.photoCaption')}
        placeholder={t('message.photoCaption')} placeholderTextColor={colors.gray600} style={[label,{minHeight:80,textAlignVertical:'top'}]}/>}
      {!!draft&&(draft.kind==='image'?<Image testID="forward-media-preview" accessibilityLabel={t('message.photo')}
        source={{uri:draft.previewUrl??draft.url}} style={{width:'100%',height:220}} resizeMode="contain"/>
        :draft.kind==='voice'?<MessageVoice url={draft.previewUrl??draft.url} mine durationSeconds={draft.durationSeconds} onLongPress={()=>{}}/>
        :<MessageVideo url={draft.previewUrl??draft.url} mine durationSeconds={draft.durationSeconds} onLongPress={()=>{}}/>)}
    </View>
    </ScrollView>
    </KeyboardAvoidingView>
  </AppSheet>;
}
